import type { ReactElement } from "react";

import { CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";

import type { OpenDocumentRequest, ReportClaim, ReportSpan, ReportVerification, StructuredReport } from "@/components/assistant/types";
import type { CitationObject } from "@/lib/stream";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Structured research report renderer.
 *
 * Renders the JSON report shape produced by Story 2's synthesize step:
 * { sections: [{ heading, claims: [{ text, spans, verification }] }] }
 *
 * Each claim displays its verification status badge (verified / unverified / unchecked).
 * Spans that carry a resolved document identity are clickable and open the source
 * document in the viewer, highlighting the quote (reuses the citation-open path).
 */

function badgeTitle(verification: ReportVerification): string | undefined {
  const parts: string[] = [];
  if (verification.fidelity) parts.push(`Quote fidelity: ${verification.fidelity}`);
  if (verification.repaired) parts.push("quote repaired to verbatim source text");
  if (verification.reason) parts.push(verification.reason);
  return parts.length > 0 ? parts.join(" — ") : undefined;
}

function BadgeWithTooltip({ badge, title }: { badge: ReactElement; title: string | undefined }) {
  if (!title) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

function VerificationBadge({ verification }: { verification: ReportVerification }) {
  switch (verification.status) {
    case "verified":
      return (
        <BadgeWithTooltip
          title={badgeTitle(verification)}
          badge={
            <span
              tabIndex={0}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
            >
              <CheckCircle2 className="size-3" />
              {verification.fidelity === "paraphrase" ? "Verified (paraphrase)" : "Verified"}
            </span>
          }
        />
      );
    case "unverified":
      return (
        <BadgeWithTooltip
          title={badgeTitle(verification)}
          badge={
            <span
              tabIndex={0}
              className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
            >
              <AlertTriangle className="size-3" />
              Unverified
            </span>
          }
        />
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          <HelpCircle className="size-3" />
          Unchecked
        </span>
      );
  }
}

function SpanCard({
  span,
  onOpenDocument
}: {
  span: ReportSpan;
  onOpenDocument?: (request: OpenDocumentRequest) => void;
}) {
  const documentId = span.document_id;
  const clickable = Boolean(documentId && onOpenDocument);
  const citation: CitationObject | undefined = clickable
    ? {
        kind: "doc_citation",
        document_id: documentId,
        version_id: span.version_id,
        version_number: span.version_number,
        filename: span.filename,
        page: span.locator?.page,
        quote: span.quote,
      }
    : undefined;

  const content = (
    <>
      &ldquo;{span.quote}&rdquo;
      {span.locator?.page != null ? <span className="ml-1 not-italic text-muted-foreground/70">(p. {span.locator.page})</span> : null}
    </>
  );

  if (!clickable || !onOpenDocument || !citation || !documentId) {
    return (
      <blockquote className="border-l-2 border-muted-foreground/30 pl-2 py-1 pr-1 text-xs italic text-muted-foreground">
        {content}
      </blockquote>
    );
  }

  return (
    <button
      type="button"
      className="w-full cursor-pointer rounded-md border-l-2 border-muted-foreground/30 pl-2 py-1 pr-1 text-left text-xs italic text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      title={`Open in document viewer: ${span.filename ?? "document"}`}
      onClick={() =>
        onOpenDocument({
          documentId,
          filename: span.filename ?? "Document",
          versionId: span.version_id,
          versionNumber: span.version_number,
          kind: "citation",
          citation,
        })
      }
    >
      {content}
    </button>
  );
}

function ClaimCard({
  claim,
  onOpenDocument,
}: {
  claim: ReportClaim;
  onOpenDocument?: (request: OpenDocumentRequest) => void;
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="flex-1 text-sm">{claim.text}</p>
        <VerificationBadge verification={claim.verification} />
      </div>
      {claim.spans.length > 0 && (
        <div className="mt-2 space-y-1">
          {claim.spans.map((span, i) => (
            <SpanCard key={`${span.quote}-${i}`} span={span} onOpenDocument={onOpenDocument} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ResearchReport({
  report,
  onOpenDocument,
}: {
  report: StructuredReport;
  onOpenDocument?: (request: OpenDocumentRequest) => void;
}) {
  if (!report.sections?.length) return null;

  const totalClaims = report.sections.reduce((sum, s) => sum + s.claims.length, 0);
  const verified = report.sections.reduce(
    (sum, s) => sum + s.claims.filter((c) => c.verification.status === "verified").length,
    0
  );
  const unverified = report.sections.reduce(
    (sum, s) => sum + s.claims.filter((c) => c.verification.status === "unverified").length,
    0
  );
  const unchecked = totalClaims - verified - unverified;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{totalClaims} claim{totalClaims === 1 ? "" : "s"}</span>
        {verified > 0 && <span className="text-primary">{verified} verified</span>}
        {unverified > 0 && <span className="text-destructive">{unverified} unverified</span>}
        {unchecked > 0 && <span>{unchecked} unchecked</span>}
      </div>

      {report.sections.map((section, si) => (
        <section key={si}>
          <h3 className="mb-2 text-sm font-semibold">{section.heading}</h3>
          <div className="space-y-2">
            {section.claims.map((claim, ci) => (
              <ClaimCard key={ci} claim={claim} onOpenDocument={onOpenDocument} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
